import { useEffect, useState } from "react";
import { Button } from "@jobber/components/Button";
import { Combobox, ComboboxOption } from "@jobber/components/Combobox";
import { Content } from "@jobber/components/Content";
import { DataTable } from "@jobber/components/DataTable";
import { Disclosure } from "@jobber/components/Disclosure";
import { Heading } from "@jobber/components/Heading";
import { InlineLabel } from "@jobber/components/InlineLabel";
import DashboardTab from "../../components/Dashboard/DashboardTab";
import {
    getListen360Users,
    getListen360Locations,
    getConnectedFranchises,
    getLocationMapping,
    attachLocationToUser,
    detachLocationFromUser,
    createLocationMapping,
    deleteLocationMapping,
} from "../../services";
import { Franchise } from "../../types/Franchise";
import { Listen360User } from "../../types/Listen360User";
import { Listen360Location } from "../../types/Listen360Location";

function Locations() {
    const [listen360Users, setListen360Users] = useState<Listen360User[]>([]);
    const [listen360Locations, setListen360Locations] = useState<Listen360Location[]>([]);
    const [franchises, setFranchises] = useState<Franchise[]>([]);
    const [attachedLocationsByUser, setAttachedLocationsByUser] = useState<Record<string, string[]>>({});
    const [selectedFranchisesByLocation, setSelectedFranchisesByLocation] = useState<
        Record<string, Record<string, ComboboxOption[]>>
    >({});
    // Maps franchiseId → mapping_id for deletion
    const [mappingIds, setMappingIds] = useState<Record<string, string>>({});
    const [mappingLoaded, setMappingLoaded] = useState(false);

    useEffect(() => {
        getListen360Users().then(setListen360Users);
        getListen360Locations().then(setListen360Locations);
        getConnectedFranchises().then(setFranchises);

        getLocationMapping().then((mapping) => {
            const locationsByUser: Record<string, string[]> = {};
            const franchisesByLocation: Record<string, Record<string, ComboboxOption[]>> = {};
            const initialMappingIds: Record<string, string> = {};

            for (const userMap of mapping.users) {
                const userId = userMap.user.id;
                locationsByUser[userId] = userMap.locations.map((loc) => loc.location.id);
                franchisesByLocation[userId] = {};
                for (const locMap of userMap.locations) {
                    franchisesByLocation[userId][locMap.location.id] = locMap.franchises.map((f) => {
                        if (f.mapping_id) {
                            initialMappingIds[f.id] = f.mapping_id;
                        }
                        return { id: f.id, label: f.name };
                    });
                }
            }

            setAttachedLocationsByUser(locationsByUser);
            setSelectedFranchisesByLocation(franchisesByLocation);
            setMappingIds(initialMappingIds);
            setMappingLoaded(true);
        });
    }, []);

    const handleLocationSelect = async (userId: string, selection: ComboboxOption[]) => {
        const currentIds = attachedLocationsByUser[userId] || [];
        const newIdSet = new Set(selection.map((loc) => String(loc.id)));

        // Find removed locations and detach them
        const removedIds = currentIds.filter((id) => !newIdSet.has(id));
        for (let i = 0; i < removedIds.length; i++) {
            const locationId = removedIds[i];
            // First delete all franchise mappings for this location
            const attachedFranchises = selectedFranchisesByLocation[userId]?.[locationId] || [];
            for (let j = 0; j < attachedFranchises.length; j++) {
                const mid = mappingIds[String(attachedFranchises[j].id)];
                if (mid) {
                    await deleteLocationMapping(mid);
                    setMappingIds((prev) => {
                        const next = { ...prev };
                        delete next[String(attachedFranchises[j].id)];
                        return next;
                    });
                }
            }

            // Then detach the location from the user
            await detachLocationFromUser(userId, locationId);

            // Clear the franchise selections from state
            setSelectedFranchisesByLocation((prev) => {
                const userLocations = { ...prev[userId] };
                delete userLocations[locationId];
                return { ...prev, [userId]: userLocations };
            });
        }

        setAttachedLocationsByUser((prev) => ({
            ...prev,
            [userId]: selection.map((loc) => String(loc.id)),
        }));
    };

    const getAttachedLocations = (userId: string): Listen360Location[] => {
        const attachedIds = attachedLocationsByUser[userId] || [];
        return listen360Locations.filter((loc) => attachedIds.includes(loc.id));
    };

    const getLocationCount = (userId: string): number => {
        return (attachedLocationsByUser[userId] || []).length;
    };

    const getFranchiseCount = (userId: string): number => {
        const userLocations = selectedFranchisesByLocation[userId] || {};
        return Object.values(userLocations).reduce((sum, franchises) => sum + franchises.length, 0);
    };

    const handleFranchiseSelect = async (userId: string, locationId: string, selection: ComboboxOption[]) => {
        const currentSelection = getSelectedFranchises(userId, locationId);
        const currentIds = new Set(currentSelection.map((f) => String(f.id)));
        const newIds = new Set(selection.map((f) => String(f.id)));

        const isFirstFranchise = currentSelection.length === 0 && selection.length > 0;
        const isLastFranchiseRemoved = currentSelection.length > 0 && selection.length === 0;

        // If this is the first franchise, attach the location to the user first
        if (isFirstFranchise) {
            await attachLocationToUser(userId, locationId);
        }

        // Create mappings for newly added franchises
        for (const franchise of selection) {
            if (!currentIds.has(String(franchise.id))) {
                const mappingId = await createLocationMapping(String(franchise.id), locationId);
                setMappingIds((prev) => ({ ...prev, [String(franchise.id)]: mappingId }));
            }
        }

        // Delete mappings for removed franchises
        for (const franchise of currentSelection) {
            if (!newIds.has(String(franchise.id))) {
                const mid = mappingIds[String(franchise.id)];
                if (mid) {
                    await deleteLocationMapping(mid);
                    setMappingIds((prev) => {
                        const next = { ...prev };
                        delete next[String(franchise.id)];
                        return next;
                    });
                }
            }
        }

        // If the last franchise was removed, detach the location from the user
        if (isLastFranchiseRemoved) {
            await detachLocationFromUser(userId, locationId);
        }

        setSelectedFranchisesByLocation((prev) => ({
            ...prev,
            [userId]: {
                ...prev[userId],
                [locationId]: selection,
            },
        }));
    };

    const getSelectedFranchises = (userId: string, locationId: string): ComboboxOption[] => {
        return selectedFranchisesByLocation[userId]?.[locationId] || [];
    };

    const getAvailableLocationsForUser = (userId: string): Listen360Location[] => {
        const currentUserLocationIds = new Set(attachedLocationsByUser[userId] || []);
        const otherUserLocationIds = new Set<string>();

        Object.entries(attachedLocationsByUser).forEach(([uid, locationIds]) => {
            if (uid !== userId) {
                locationIds.forEach((id) => otherUserLocationIds.add(id));
            }
        });

        return listen360Locations.filter(
            (loc) => !otherUserLocationIds.has(loc.id) || currentUserLocationIds.has(loc.id)
        );
    };

    const getSelectedLocationsForUser = (userId: string): ComboboxOption[] => {
        const attachedIds = attachedLocationsByUser[userId] || [];
        return attachedIds.map((id) => {
            const location = listen360Locations.find((loc) => loc.id === id);
            return { id, label: location?.name || id };
        });
    };

    const getAvailableFranchises = (currentUserId: string, currentLocationId: string): Franchise[] => {
        const currentSelection = getSelectedFranchises(currentUserId, currentLocationId);
        const currentSelectionIds = new Set(currentSelection.map((f) => String(f.id)));

        const assignedIds = new Set<string>();
        Object.entries(selectedFranchisesByLocation).forEach(([userId, locations]) => {
            Object.entries(locations).forEach(([locationId, selectedFranchises]) => {
                if (userId === currentUserId && locationId === currentLocationId) return;
                selectedFranchises.forEach((f) => assignedIds.add(String(f.id)));
            });
        });

        return franchises.filter(
            (f) => !assignedIds.has(f.id) || currentSelectionIds.has(f.id)
        );
    };

    return (
        <DashboardTab>
            <Content spacing="small">
                {!mappingLoaded ? null : [...listen360Users].sort((a, b) => {
                    const aCount = (attachedLocationsByUser[a.id] || []).length;
                    const bCount = (attachedLocationsByUser[b.id] || []).length;
                    if (aCount === 0 && bCount > 0) return 1;
                    if (aCount > 0 && bCount === 0) return -1;
                    return 0;
                }).map((user) => (
                    <Disclosure
                        key={user.id}
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Heading level={3}>{user.name}</Heading>
                                {getLocationCount(user.id) === 0 && getFranchiseCount(user.id) === 0 ? (
                                    <InlineLabel color="red">No locations configured</InlineLabel>
                                ) : (
                                    <>
                                        <InlineLabel color="orange">
                                            {getLocationCount(user.id)} {getLocationCount(user.id) === 1 ? "location" : "locations"}
                                        </InlineLabel>
                                        <InlineLabel color="green">
                                            {getFranchiseCount(user.id)} {getFranchiseCount(user.id) === 1 ? "franchise" : "franchises"}
                                        </InlineLabel>
                                    </>
                                )}
                            </div>
                        }
                        defaultOpen={getLocationCount(user.id) > 0}
                        UNSAFE_style={{
                            summary: { padding: "12px 16px" },
                            content: { padding: "12px 16px 16px" },
                        }}
                    >
                            <Content spacing="base">
                                <Combobox
                                    multiSelect
                                    selected={getSelectedLocationsForUser(user.id)}
                                    onSelect={(selection) => handleLocationSelect(user.id, selection)}
                                    subjectNoun="locations"
                                >
                                    <Combobox.Activator>
                                        {({ open }) => (
                                            <Button
                                                label="Select Locations"
                                                onClick={open}
                                                type="secondary"
                                                variation="learning"
                                            />
                                        )}
                                    </Combobox.Activator>
                                    {getAvailableLocationsForUser(user.id).map((location) => (
                                        <Combobox.Option
                                            key={location.id}
                                            id={location.id}
                                            label={location.name}
                                        />
                                    ))}
                                </Combobox>
                                {getAttachedLocations(user.id).length > 0 && (
                                    <DataTable.Container>
                                        <DataTable.Table style={{ tableLayout: "fixed", width: "100%" }}>
                                            <colgroup>
                                                <col style={{ width: "20%" }} />
                                                <col style={{ width: "80%" }} />
                                            </colgroup>
                                            <DataTable.Body>
                                                {getAttachedLocations(user.id).map((location) => (
                                                    <DataTable.Row key={location.id}>
                                                        <DataTable.Cell>
                                                            <strong>{location.name}</strong>
                                                        </DataTable.Cell>
                                                        <DataTable.Cell>
                                                            <Combobox
                                                                multiSelect
                                                                selected={getSelectedFranchises(user.id, location.id)}
                                                                onSelect={(selection) =>
                                                                    handleFranchiseSelect(user.id, location.id, selection)
                                                                }
                                                                label="Franchises"
                                                                subjectNoun="franchises"
                                                            >
                                                                {getAvailableFranchises(user.id, location.id).map((franchise) => (
                                                                    <Combobox.Option
                                                                        key={franchise.id}
                                                                        id={franchise.id}
                                                                        label={franchise.name}
                                                                    />
                                                                ))}
                                                            </Combobox>
                                                        </DataTable.Cell>
                                                    </DataTable.Row>
                                                ))}
                                            </DataTable.Body>
                                        </DataTable.Table>
                                    </DataTable.Container>
                                )}
                            </Content>
                    </Disclosure>
                ))}
            </Content>

        </DashboardTab>
    );
}

export default Locations;
